'use client';

import useCurrentUser from "@/hooks/useCurrentUser";
import { useAuthUser } from "@/hooks/useAuthUser";
import { firebaseAuth } from "@/lib/Firebase";
import { InstituitionService } from "@/lib/Institutions";
import { Institution } from "@/lib/Institutions/types";
import StringUtils from "@/utils/StringUtils";
import { useQuery } from "@tanstack/react-query";
import Image from "next/image";
import React from "react";

type InstitutionLogoProps = {
  institution?: Institution;
  id?: string;
  name?: string;
  height?: number;
  width?: number;
  showName?: boolean;
};


function InstitutionLogo({ institution, id, height = 64, width = 64, showName = false }: InstitutionLogoProps) {
  const { data: authUser } = useAuthUser(['authUser'], firebaseAuth);
  const { data: user } = useCurrentUser(!!authUser);
  const institutionId = institution?.id ?? id ?? user?.account?.studentStatus?.institutions?.[0];

  const { data } = useQuery({
    queryKey: ['institution', institutionId || '__placeholder__'],
    queryFn: () => InstituitionService.getIntitutionById(institutionId as string),
    enabled: !!institutionId,
    staleTime: 10 * 60 * 1000,
    select(data) {
      if (!data) return null;
      return {
        ...data,
        name: StringUtils.capitalizePhrase(data?.name as string),
      };
    },
  });

  if (!data) return null;

  return (
    <div className="relative flex flex-col place-items-center gap-2 mx-auto">
      <Image
        src={`https://img.logokit.com/${data?.domains?.[0]}?format=png&token=${process.env.NEXT_PUBLIC_LOGO_KIT_PUBLIC_KEY}`}
        alt={institution?.name ?? data?.name}
        height={height}
        width={width}
        unoptimized
        className="rounded-full" />

      {showName && <div className="absolute top-1/2 left-1/2 z-10 -translate-x-1/2 -translate-y-1/2 size-28">
        <svg className='w-full h-full' viewBox="0 0 200 200">
          <defs>
            <path
              id="invertedCirclePath"
              d="M 100, 100 m -80, 0 a 80,80 0 1,0 160,0 a 80,80 0 1,0 -160,0"
            />
          </defs>
          <text>
            <textPath
              className="fill-secondary tracking-[0.33em] text-sm uppercase"
              href="#invertedCirclePath"
              startOffset="25%"
              textAnchor="middle"
            >
              {institution?.name ?? data?.name}
            </textPath>
          </text>
        </svg>
      </div>}
    </div>
  );
}

export default React.memo(InstitutionLogo);